import { useParams, Link } from 'react-router-dom';
import { AlertTriangle, Info } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/PageHero';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { EXPORT_PAGES, GRADING_DETAIL } from '../lib/pageContent';
import { useLiveCompliance } from '../lib/useLiveCompliance';
import { EXPORT_PAGE } from '../constants/testIds';

const quoteLinkFor = (page) => {
  if (page.domestic) return '/#order-form';
  const params = new URLSearchParams();
  if (page.country) params.set('country', page.country);
  if (page.port) params.set('port', page.port);
  const qs = params.toString();
  return qs ? `/request-quote?${qs}` : '/request-quote';
};

export default function ExportDestinationPage() {
  const { slug } = useParams();
  const page = EXPORT_PAGES.find((p) => p.slug === slug);
  const liveCompliance = useLiveCompliance(slug);

  useDocumentMeta(page ? page.metaTitle : 'Page Not Found | BulkUsedTires.com', page ? page.metaDescription : undefined, `/export/${slug}`, page?.draft);

  if (!page) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-40 pb-24 text-center">
          <h1 className="font-display font-extrabold uppercase text-3xl text-brand-text">Page Not Found</h1>
          <Link to="/" className="mt-5 inline-block text-brand-orange underline">Return Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const relatedPages = (page.relatedSlugs || [])
    .map((s) => EXPORT_PAGES.find((p) => p.slug === s && !p.draft))
    .filter(Boolean);

  // Prefer the live, monthly-refreshed compliance doc from MongoDB; fall
  // back to the bundled static copy in pageContent.js if the fetch hasn't
  // resolved yet or failed.
  const compliance = liveCompliance || page.compliance;

  return (
    <div className="min-h-screen bg-brand-bg">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <Navbar />

      {page.draft && (
        <div data-testid={EXPORT_PAGE.draftBanner} className="bg-amber-500/15 border-b border-amber-500/40 text-amber-300 text-sm text-center py-3 px-5">
          <strong className="uppercase tracking-wide">Draft — Not Yet Published.</strong>{' '}
          This destination is pending verification and is not indexed or linked from the live site.
        </div>
      )}

      <PageHero eyebrow={page.eyebrow} title={page.h1} description={page.intro} />

      <section data-testid={EXPORT_PAGE.section} className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {page.highlights.map((h) => (
              <div key={h.label} className="rounded-2xl border border-brand-border bg-brand-surface p-5">
                <p className="font-mono text-xs uppercase tracking-wider text-brand-amber">{h.label}</p>
                <p className="mt-1.5 font-display font-bold text-brand-text text-sm sm:text-base">{h.value}</p>
              </div>
            ))}
          </div>

          {page.image && (
            <img
              src={page.image.url}
              alt={page.image.alt}
              loading="lazy"
              width="1200"
              height="500"
              className="mt-10 w-full h-56 sm:h-72 object-cover rounded-2xl border border-brand-border"
            />
          )}

          {/* Products & Tire Grades Available */}
          <div className="mt-10">
            <h2 className="font-display font-bold uppercase text-brand-text text-xl sm:text-2xl mb-2">Products &amp; Tire Grades Available</h2>
            <p className="text-brand-muted text-sm sm:text-base mb-5">
              Every {page.domestic ? 'order' : 'container'} is pulled from our 300+ stocked sizes across passenger, SUV, performance and commercial
              truck categories, sorted into three consistent grades.
              {page.highlights.find((h) => h.label === 'Popular Grade') && ' ' + page.highlights.find((h) => h.label === 'Popular Grade').value + ' is the most requested grade for this market.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {GRADING_DETAIL.map((g) => (
                <div key={g.grade} className="rounded-xl border border-brand-border bg-brand-surface p-5">
                  <p className="font-display font-bold text-brand-text text-sm">{g.grade}</p>
                  <p className="mt-1 font-mono text-xs text-brand-amber">{g.tread} · {g.pct}</p>
                  <p className="mt-2 text-brand-muted text-xs leading-relaxed">{g.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Container & Shipping Options */}
          <div className="mt-10">
            <h2 className="font-display font-bold uppercase text-brand-text text-xl sm:text-2xl mb-2">
              {page.domestic ? 'Delivery & Packaging Options' : 'Container & Shipping Options'}
            </h2>
            {page.domestic ? (
              <p className="text-brand-muted text-sm sm:text-base leading-relaxed">
                Domestic orders ship full-truckload, palletized by grade and size, in the same three package tiers we use for export: an 800-tire
                starter load, a 1,600-tire load, or a 2,400-tire load for high-volume buyers. There is no ocean freight or container booking step —
                your order moves straight from our Ohio warehouse to your dock.
              </p>
            ) : (
              <p className="text-brand-muted text-sm sm:text-base leading-relaxed">
                We load a standard 20ft container (roughly 800 tires loose-loaded), a 40ft container (roughly 1,600 tires), or a 40ft High-Cube
                container using high-density tripling — nesting up to three tires inside one another — to fit up to 2,400 tires and lower your
                per-unit freight cost. {page.port ? `Every container is booked through to ${page.port}, ` : ''}with a commercial invoice, packing
                list and bill-of-lading coordination prepared for customs clearance.
              </p>
            )}
          </div>

          {/* Import Compliance Guidance */}
          {compliance && (
            <div
              data-testid={EXPORT_PAGE.complianceSection}
              className={`mt-10 rounded-2xl border p-7 sm:p-9 ${compliance.alert ? 'border-red-500/40 bg-red-500/10' : 'border-brand-border bg-brand-surface'}`}
            >
              <div className="flex items-center gap-2.5">
                {compliance.alert ? <AlertTriangle className="w-5 h-5 text-red-400" /> : <Info className="w-5 h-5 text-brand-amber" />}
                <h2 className={`font-display font-bold uppercase text-lg sm:text-xl ${compliance.alert ? 'text-red-300' : 'text-brand-text'}`}>
                  {compliance.heading}
                </h2>
              </div>
              <div className="mt-4 space-y-3">
                {compliance.body.map((para) => (
                  <p key={para} className={`text-sm sm:text-base leading-relaxed ${compliance.alert ? 'text-red-300/90' : 'text-brand-muted'}`}>
                    {para}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 rounded-2xl border border-brand-border bg-brand-surface p-7 sm:p-9">
            <h2 className="font-display font-bold uppercase text-brand-text text-xl sm:text-2xl">Why Buyers Choose Us</h2>
            <ul className="mt-5 space-y-2.5">
              {page.whyUs.map((point) => (
                <li key={point} className="flex gap-3 text-brand-muted text-sm sm:text-base">
                  <span className="text-brand-amber mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-amber shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-12">
            <h2 className="font-display font-bold uppercase text-brand-text text-xl sm:text-2xl mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {page.faq.map((item, i) => (
                <div key={item.q} data-testid={EXPORT_PAGE.faqItem(i)} className="border-b border-brand-border pb-5">
                  <h3 className="font-display font-bold uppercase text-brand-text text-base sm:text-lg">{item.q}</h3>
                  <p className="mt-2 text-brand-muted text-sm sm:text-base leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 rounded-2xl border border-brand-border bg-brand-surface2 p-7 sm:p-9 text-center">
            <p className="font-display font-bold uppercase text-brand-text text-xl sm:text-2xl">Ready to request a quote?</p>
            <p className="mt-2 text-brand-muted text-sm sm:text-base">
              {page.domestic
                ? 'Tell us your target package and sizes — we will confirm pricing and truckload delivery.'
                : `Tell us your target package and sizes — we will confirm pricing, grading and freight to ${page.port || 'your destination'}.`}
            </p>
            <Link
              to={quoteLinkFor(page)}
              data-testid={EXPORT_PAGE.ctaButton}
              className="mt-5 inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors"
            >
              {page.ctaLabel || 'Request a Quote'}
            </Link>
            {page.ctaNote && <p className="mt-3 text-brand-muted text-xs max-w-md mx-auto">{page.ctaNote}</p>}
          </div>

          {relatedPages.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display font-bold uppercase text-brand-text text-lg mb-4">Related Export Destinations</h2>
              <div className="flex flex-wrap gap-3">
                {relatedPages.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/export/${p.slug}`}
                    className="font-mono text-xs uppercase tracking-wider text-brand-muted border border-brand-border rounded-full px-4 py-2 hover:border-brand-orange hover:text-brand-orange transition-colors"
                  >
                    {p.navLabel}
                  </Link>
                ))}
                <Link
                  to="/export"
                  className="font-mono text-xs uppercase tracking-wider text-brand-amber border border-brand-border rounded-full px-4 py-2 hover:border-brand-amber transition-colors"
                >
                  View All Destinations
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
